import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loading, MessageBanner, RadioButton, RadioButtonGroup, TextArea, TextField, } from "@folio/stripes/components";
import { FormattedMessage, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useOkapiKy } from "@folio/stripes/core";
import { AgendaChangeModal } from "./AgendaChangeModal";
import { AgendaHandlerChoice } from "./AgendaHandlerChoice";
import { RecekSelect } from "../inputs";
import { LINKABLE_PERIODICALS_LIMIT, createSpecialIssueLink, fetchLinkableIssues, fetchLinkableVolumes, fetchLinkablePeriodicals, linkIssueToMonograph, } from "../../api/agendaService";
import { defaultVolumeId } from "../../utils/linkableIssues";
import { isSpecialKind } from "../../utils/issueKind";
import { formatDate } from "../../utils/formatUtils";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useServerErrorMessage } from "../../hooks/useServerErrorMessage";
import { LINKABLE_ISSUES_KEY, LINKABLE_PERIODICALS_KEY, LINKABLE_VOLUMES_KEY, } from "../../utils/agendaQueries";
import css from "./Agenda.module.css";
var NEW_ISSUE = "new-issue";
var SEARCH_MIN_LENGTH = 2;
var INPUT_DEBOUNCE_MS = 400;
var NO_VOLUMES = [];
var NO_ISSUES = [];
var periodicalLabel = function (periodical) {
    var _a;
    return [
        (_a = periodical.name) !== null && _a !== void 0 ? _a : "–",
        periodical.issn ? "(".concat(periodical.issn, ")") : null,
        periodical.publisherName ? "- ".concat(periodical.publisherName) : null,
    ]
        .filter(Boolean)
        .join(" ");
};
var issueLabel = function (issue) {
    return issue.expectedDate
        ? "".concat(issue.label, " (").concat(formatDate(issue.expectedDate), ")")
        : issue.label;
};
var parseIssueNumber = function (value) {
    var parsed = Number(value.trim());
    return value.trim() && Number.isInteger(parsed) && parsed > 0
        ? parsed
        : undefined;
};
export var LinkToPeriodicalDialog = function (_a) {
    var _b, _c, _d, _e, _f, _g;
    var open = _a.open, monographTitleId = _a.monographTitleId, monographName = _a.monographName, onClose = _a.onClose, onChanged = _a.onChanged;
    var formatMessage = useIntl().formatMessage;
    var kyInstance = useOkapiKy();
    var _h = useState(""), search = _h[0], setSearch = _h[1];
    var _j = useState(null), periodicalId = _j[0], setPeriodicalId = _j[1];
    var _k = useState(""), pickedVolumeId = _k[0], setPickedVolumeId = _k[1];
    var _l = useState(null), issueChoice = _l[0], setIssueChoice = _l[1];
    var _m = useState(""), newLabel = _m[0], setNewLabel = _m[1];
    var _o = useState(""), newNumber = _o[0], setNewNumber = _o[1];
    var _p = useState(""), specialNote = _p[0], setSpecialNote = _p[1];
    var _q = useState(null), handler = _q[0], setHandler = _q[1];
    var _r = useState(""), note = _r[0], setNote = _r[1];
    useEffect(function () {
        if (!open) {
            setSearch("");
            setPeriodicalId(null);
            setPickedVolumeId("");
            setIssueChoice(null);
            setNewLabel("");
            setNewNumber("");
            setSpecialNote("");
            setHandler(null);
            setNote("");
        }
    }, [open]);
    var debouncedSearch = useDebouncedValue(search.trim(), INPUT_DEBOUNCE_MS);
    var debouncedLabel = useDebouncedValue(newLabel.trim(), INPUT_DEBOUNCE_MS);
    var debouncedNumber = useDebouncedValue(newNumber, INPUT_DEBOUNCE_MS);
    var periodicalsQuery = useQuery([LINKABLE_PERIODICALS_KEY, debouncedSearch], function () { return fetchLinkablePeriodicals(kyInstance, debouncedSearch); }, {
        enabled: open && debouncedSearch.length >= SEARCH_MIN_LENGTH,
        keepPreviousData: true,
    });
    var periodicals = (_c = (_b = periodicalsQuery.data) === null || _b === void 0 ? void 0 : _b.items) !== null && _c !== void 0 ? _c : [];
    var volumesQuery = useQuery([LINKABLE_VOLUMES_KEY, periodicalId], function () { return fetchLinkableVolumes(kyInstance, periodicalId !== null && periodicalId !== void 0 ? periodicalId : ""); }, { enabled: open && !!periodicalId });
    var volumes = (_e = (_d = volumesQuery.data) === null || _d === void 0 ? void 0 : _d.volumes) !== null && _e !== void 0 ? _e : NO_VOLUMES;
    var volumeId = pickedVolumeId || defaultVolumeId(volumes);
    var issuesQuery = useQuery([LINKABLE_ISSUES_KEY, periodicalId, volumeId], function () { return fetchLinkableIssues(kyInstance, periodicalId !== null && periodicalId !== void 0 ? periodicalId : "", volumeId); }, { enabled: open && !!periodicalId && !!volumeId });
    var volumeOptions = useMemo(function () {
        return volumes.map(function (volume) { return ({
            value: volume.volumeId,
            label: formatMessage({ id: "ui-br-legaldeposits.agenda.linkPeriodical.volume.option" }, {
                label: volume.label,
                linkable: volume.linkableCount,
                freeSpecial: volume.freeSpecialCount,
            }),
        }); });
    }, [volumes, formatMessage]);
    var issues = useMemo(function () { var _a, _b; return (_b = (_a = issuesQuery.data) === null || _a === void 0 ? void 0 : _a.volume.issues) !== null && _b !== void 0 ? _b : NO_ISSUES; }, [issuesQuery.data]);
    var issueOptionLabel = useCallback(function (issue) {
        return isSpecialKind(issue.kind)
            ? formatMessage({ id: "ui-br-legaldeposits.agenda.linkPeriodical.issue.special" }, { issue: issueLabel(issue) })
            : issueLabel(issue);
    }, [formatMessage]);
    var periodicalsError = useServerErrorMessage(periodicalsQuery.error, "ui-br-legaldeposits.agenda.linkPeriodical.searchFailed");
    var volumesError = useServerErrorMessage(volumesQuery.error, "ui-br-legaldeposits.agenda.linkPeriodical.issuesFailed");
    var issuesError = useServerErrorMessage(issuesQuery.error, "ui-br-legaldeposits.agenda.linkPeriodical.issuesFailed");
    var isNewIssue = issueChoice === NEW_ISSUE;
    var newIssueNumber = parseIssueNumber(debouncedNumber);
    var newNumberInvalid = newNumber.trim() !== "" && !parseIssueNumber(newNumber);
    var inputsSettled = debouncedLabel === newLabel.trim() && debouncedNumber === newNumber;
    var noteValue = note.trim() || undefined;
    var canSubmit = !!periodicalId &&
        !!volumeId &&
        handler !== null &&
        (isNewIssue
            ? debouncedLabel.length > 0 && !newNumberInvalid && inputsSettled
            : !!issueChoice);
    var run = useCallback(function (dryRun) {
        if (!periodicalId || !handler || !issueChoice) {
            return Promise.reject(new Error("A periodical, an issue and a handler must be chosen"));
        }
        if (issueChoice === NEW_ISSUE) {
            return createSpecialIssueLink(kyInstance, monographTitleId, {
                periodicalTitleId: periodicalId,
                label: debouncedLabel,
                volumeId: volumeId || undefined,
                issueNumber: newIssueNumber,
                specialNote: specialNote.trim() || undefined,
                agenda: handler,
                note: noteValue,
            }, dryRun);
        }
        return linkIssueToMonograph(kyInstance, issueChoice, { monographTitleId: monographTitleId, agenda: handler, note: noteValue }, dryRun);
    }, [
        kyInstance,
        monographTitleId,
        periodicalId,
        handler,
        issueChoice,
        debouncedLabel,
        volumeId,
        newIssueNumber,
        specialNote,
        noteValue,
    ]);
    var previewKey = useMemo(function () { return [
        "link-periodical",
        monographTitleId,
        periodicalId,
        volumeId,
        issueChoice,
        handler,
        isNewIssue ? debouncedLabel : null,
        isNewIssue ? (newIssueNumber !== null && newIssueNumber !== void 0 ? newIssueNumber : null) : null,
    ]; }, [
        monographTitleId,
        periodicalId,
        volumeId,
        issueChoice,
        handler,
        isNewIssue,
        debouncedLabel,
        newIssueNumber,
    ]);
    var handleSearchChange = useCallback(function (e) { return setSearch(e.target.value); }, []);
    var handlePeriodicalChange = useCallback(function (value) { return function () {
        setPeriodicalId(value);
        setPickedVolumeId("");
        setIssueChoice(null);
    }; }, []);
    var handleVolumeChange = useCallback(function (e) {
        setPickedVolumeId(e.target.value);
        setIssueChoice(null);
    }, []);
    var handleIssueChange = useCallback(function (value) { return function () { return setIssueChoice(value); }; }, []);
    var handleNewLabelChange = useCallback(function (e) { return setNewLabel(e.target.value); }, []);
    var handleNewNumberChange = useCallback(function (e) { return setNewNumber(e.target.value); }, []);
    var handleSpecialNoteChange = useCallback(function (e) { return setSpecialNote(e.target.value); }, []);
    var handleNoteChange = useCallback(function (e) { return setNote(e.target.value); }, []);
    var searchTooShort = debouncedSearch.length < SEARCH_MIN_LENGTH;
    var moreResults = ((_g = (_f = periodicalsQuery.data) === null || _f === void 0 ? void 0 : _f.totalRecords) !== null && _g !== void 0 ? _g : 0) > LINKABLE_PERIODICALS_LIMIT;
    return (React.createElement(AgendaChangeModal, { open: open, heading: formatMessage({ id: "ui-br-legaldeposits.agenda.linkPeriodical.heading" }, { name: monographName }), previewKey: previewKey, canSubmit: canSubmit, run: run, impactScope: "linkedPair", onClose: onClose, onChanged: onChanged },
        React.createElement("p", null,
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.linkPeriodical.intro" })),
        React.createElement(TextField, { label: formatMessage({
                id: "ui-br-legaldeposits.agenda.linkPeriodical.search.label",
            }), placeholder: formatMessage({
                id: "ui-br-legaldeposits.agenda.linkPeriodical.search.placeholder",
            }), value: search, onChange: handleSearchChange, "data-test-id": "link-periodical-search" }),
        searchTooShort && !periodicalId && (React.createElement("p", { className: css.hint },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.linkPeriodical.search.hint" }))),
        !searchTooShort && periodicalsQuery.isFetching && React.createElement(Loading, null),
        periodicalsError && (React.createElement(MessageBanner, { type: "error" }, periodicalsError)),
        !searchTooShort &&
            periodicalsQuery.isSuccess &&
            periodicals.length === 0 && (React.createElement("p", { className: css.hint },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.linkPeriodical.search.empty" }))),
        !searchTooShort && periodicals.length > 0 && (React.createElement(RadioButtonGroup, { label: formatMessage({
                id: "ui-br-legaldeposits.agenda.linkPeriodical.periodical.label",
            }) }, periodicals.map(function (periodical) { return (React.createElement(RadioButton, { key: periodical.titleId, name: "link-periodical-target", label: periodicalLabel(periodical), checked: periodicalId === periodical.titleId, onChange: handlePeriodicalChange(periodical.titleId) })); }))),
        !searchTooShort && moreResults && (React.createElement("p", { className: css.hint },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.linkPeriodical.search.more", values: { count: LINKABLE_PERIODICALS_LIMIT } }))),
        periodicalId && volumesQuery.isLoading && React.createElement(Loading, null),
        volumesError && (React.createElement(MessageBanner, { type: "error" }, volumesError)),
        volumesQuery.isSuccess && (React.createElement("div", { className: css.section },
            volumeOptions.length > 0 ? (React.createElement(RecekSelect, { labelId: "ui-br-legaldeposits.agenda.linkPeriodical.volume.label", value: volumeId, dataOptions: volumeOptions, onChange: handleVolumeChange })) : (React.createElement("p", { className: css.hint },
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.linkPeriodical.volume.none" }))),
            issuesQuery.isFetching && React.createElement(Loading, null),
            issuesError && (React.createElement(MessageBanner, { type: "error" }, issuesError)),
            issuesQuery.isSuccess && (React.createElement(RadioButtonGroup, { label: formatMessage({
                    id: "ui-br-legaldeposits.agenda.linkPeriodical.issue.label",
                }) },
                issues.map(function (issue) { return (React.createElement(RadioButton, { key: issue.id, name: "link-periodical-issue", label: issue.linked
                        ? formatMessage({
                            id: "ui-br-legaldeposits.agenda.linkPeriodical.issue.taken",
                        }, { issue: issueOptionLabel(issue) })
                        : issueOptionLabel(issue), checked: issueChoice === issue.id, disabled: !issue.linkable, onChange: handleIssueChange(issue.id) })); }),
                React.createElement(RadioButton, { name: "link-periodical-issue", label: formatMessage({
                        id: "ui-br-legaldeposits.agenda.linkPeriodical.issue.new",
                    }), checked: isNewIssue, onChange: handleIssueChange(NEW_ISSUE) }))),
            issuesQuery.isSuccess && issues.length === 0 && (React.createElement("p", { className: css.hint },
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.agenda.linkPeriodical.issue.none" }))),
            isNewIssue && (React.createElement(React.Fragment, null,
                React.createElement(TextField, { label: formatMessage({
                        id: "ui-br-legaldeposits.agenda.linkPeriodical.new.label",
                    }), value: newLabel, onChange: handleNewLabelChange, required: true }),
                React.createElement(TextField, { label: formatMessage({
                        id: "ui-br-legaldeposits.agenda.linkPeriodical.new.number",
                    }), value: newNumber, onChange: handleNewNumberChange, error: newNumberInvalid
                        ? formatMessage({
                            id: "ui-br-legaldeposits.agenda.linkPeriodical.new.numberInvalid",
                        })
                        : undefined }),
                React.createElement(TextField, { label: formatMessage({
                        id: "ui-br-legaldeposits.agenda.linkPeriodical.new.specialNote",
                    }), value: specialNote, onChange: handleSpecialNoteChange }))))),
        volumesQuery.isSuccess && (React.createElement(AgendaHandlerChoice, { name: "link-periodical-handler", value: handler, onChange: setHandler })),
        React.createElement(TextArea, { label: formatMessage({
                id: "ui-br-legaldeposits.agenda.dialog.note.label",
            }), value: note, onChange: handleNoteChange })));
};
